/* SignatureMaker.jsx
 * External deps (unchanged):
 *   yarn add react-pdf react-rnd pdfjs-dist pdf-lib downloadjs axios
 *
 * Changes vs. previous version
 * ─────────────────────────────────────────────
 * ▸ Viewer still fills nearly the entire viewport (`height: calc(100vh - 260px)`)
 * ▸ Vertical scrollbar logic restored: appears only when the zoomed page is taller
 */
import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Slider,
} from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, degrees } from "pdf-lib";
import { Rnd } from "react-rnd";
import download from "downloadjs";
import axios from "axios";
import Cookies from "universal-cookie";

import { people } from "./sections/variables/people.js";
import { useSiteDataApi } from "../utilities/SiteDataProvider";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const cookies = new Cookies();
const FILE_TYPES = ["PDF"];

/* helpers */
const b64ToBuf = (b64) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
const bytesToBase64 = (bytes) => {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
};
const cornerHandle = {
  width: 12,
  height: 12,
  border: "2px solid #fff",
  borderRadius: "50%",
  background: "transparent",
};

export default function SignatureMaker() {
  const [siteData] = useSiteDataApi();
  const [username] = useState(cookies.get("username") || "???");

  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [numPages, setNumPages] = useState(1);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [zoom, setZoom] = useState(Number(cookies.get("zoom") || 100));

  const [hScroll, setHScroll] = useState(false);
  const [vScroll, setVScroll] = useState(false); // ← restored

  const outerRef = useRef(null);
  const pageWrapRef = useRef(null);
  const canvasDims = useRef({ w: 0, h: 0 });

  const [sigs, setSigs] = useState([]);

  /* drag & drop */
  const handleFileChosen = (file) => {
    setLoading(true);
    const fr = new FileReader();
    fr.onloadend = () => {
      setFileData(fr.result);
      setFileName(file.name);
      setErr("");
      setSigs([]);
      setLoading(false);
    };
    fr.readAsArrayBuffer(file);
  };

  /* PDF events */
  const onPdfLoaded = ({ numPages }) => {
    setNumPages(numPages);
    setPageNum((p) => Math.min(p, numPages));
  };
  const onPageRender = () => {
    const canvas = pageWrapRef.current?.querySelector("canvas");
    if (canvas && outerRef.current) {
      const { offsetWidth: w, offsetHeight: h } = canvas;
      canvasDims.current = { w, h };
      setHScroll(w > outerRef.current.clientWidth);
      setVScroll(h > outerRef.current.clientHeight); // ← scroll condition
    }
  };

  /* helpers */
  const toPx = (sig) => ({
    x: sig.x * canvasDims.current.w,
    y: sig.y * canvasDims.current.h,
    w: sig.w * canvasDims.current.w,
    h: sig.h * canvasDims.current.h,
  });
  const addSig = (person) => {
    if (!canvasDims.current.w) onPageRender();
    if (!canvasDims.current.w) return;
    const defaultW = 0.25;
    const defaultH = defaultW * (200 / 800);
    setSigs((p) => [
      ...p,
      {
        id: Date.now(),
        page: pageNum,
        x: 0.35,
        y: 0.35,
        w: defaultW,
        h: defaultH,
        imgB64: person.signature,
        name: person.name,
      },
    ]);
  };
  const deleteSig = (id) => setSigs((p) => p.filter((s) => s.id !== id));

  /* embed & download (unchanged) */
  const embedAndDownload = useCallback(async () => {
    if (!fileData || sigs.length === 0) return;
    const doc = await PDFDocument.load(fileData, { ignoreEncryption: true });

    const cache = {};
    for (const sig of sigs) {
      if (!cache[sig.imgB64])
        cache[sig.imgB64] = await doc.embedPng(b64ToBuf(sig.imgB64));
    }

    sigs.forEach((sig) => {
      const pg = doc.getPages()[sig.page - 1];
      const rot = pg.getRotation().angle;
      if (rot !== 0) pg.setRotation(degrees(0));

      const { width: pw, height: ph } = pg.getSize();
      pg.drawImage(cache[sig.imgB64], {
        x: sig.x * pw,
        y: ph - sig.y * ph - sig.h * ph,
        width: sig.w * pw,
        height: sig.h * ph,
      });
      if (rot !== 0) pg.setRotation(degrees(rot));
    });

    const pdfBytes = await doc.save();
    download(
      new Blob([pdfBytes], { type: "application/pdf" }),
      `${fileName.replace(/\.pdf$/i, "")}-signed.pdf`,
      "application/pdf"
    );

    (async () => {
      const b64 = bytesToBase64(pdfBytes);
      const signerList = [...new Set(sigs.map((s) => s.name))].join(", ");
      try {
        await axios.post(
          "/api/signature",
          JSON.stringify({
            file: b64,
            fileName,
            username,
            signer: signerList,
          }),
          {
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": siteData.thhcsfrtoken,
            },
          }
        );
      } catch (e) {
        console.error("Slack/POST failed:", e.message);
      }
    })();
  }, [fileData, sigs, fileName, siteData, username]);

  /* UI */
  return (
    <Box m={3}>
      {/* drag zone */}
      <Box sx={{ mb: 3 }}>
        <FileUploader
          types={FILE_TYPES}
          handleChange={handleFileChosen}
          name="file"
          multiple={false}
        />
      </Box>

      {loading && (
        <Grid container direction="column" alignItems="center" p={4}>
          <CircularProgress />
          <Typography mt={2}>Validating file…</Typography>
        </Grid>
      )}

      {err && (
        <Box mt={3} p={2} border="1px solid #f00" fontSize={18}>
          {err}
        </Box>
      )}

      {fileData && !loading && (
        <Grid container spacing={2}>
          {/* viewer */}
          <Grid item sm={8}>
            <Box
              ref={outerRef}
              sx={{
                width: "100%",
                height: "calc(100vh - 260px)",
                background: "#eee",
                overflowX: hScroll ? "auto" : "hidden",
                overflowY: vScroll ? "auto" : "hidden", // dynamic again
                position: "relative",
              }}
            >
              <Document
                file={{ data: fileData }}
                onLoadSuccess={onPdfLoaded}
                loading={<CircularProgress />}
              >
                <Box
                  ref={pageWrapRef}
                  sx={{
                    position: "relative",
                    width: "fit-content",
                    margin: "0 auto",
                  }}
                >
                  <Page
                    key={`${pageNum}-${zoom}`}
                    pageNumber={pageNum}
                    scale={zoom / 100}
                    onRenderSuccess={onPageRender}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />

                  {/* overlay */}
                  {canvasDims.current.w > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: canvasDims.current.w,
                        height: canvasDims.current.h,
                      }}
                    >
                      {sigs
                        .filter((s) => s.page === pageNum)
                        .map((sig) => {
                          const px = toPx(sig);
                          return (
                            <Rnd
                              key={sig.id}
                              bounds="parent"
                              lockAspectRatio
                              enableResizing={{
                                topLeft: true,
                                topRight: true,
                                bottomLeft: true,
                                bottomRight: true,
                              }}
                              handleStyles={{
                                topLeft: cornerHandle,
                                topRight: cornerHandle,
                                bottomLeft: cornerHandle,
                                bottomRight: cornerHandle,
                              }}
                              size={{ width: px.w, height: px.h }}
                              position={{ x: px.x, y: px.y }}
                              onDragStop={(_, d) =>
                                setSigs((p) =>
                                  p.map((s) =>
                                    s.id === sig.id
                                      ? {
                                          ...s,
                                          x: d.x / canvasDims.current.w,
                                          y: d.y / canvasDims.current.h,
                                        }
                                      : s
                                  )
                                )
                              }
                              onResizeStop={(_, __, ref, ___, pos) =>
                                setSigs((p) =>
                                  p.map((s) =>
                                    s.id === sig.id
                                      ? {
                                          ...s,
                                          x: pos.x / canvasDims.current.w,
                                          y: pos.y / canvasDims.current.h,
                                          w:
                                            ref.offsetWidth /
                                            canvasDims.current.w,
                                          h:
                                            ref.offsetHeight /
                                            canvasDims.current.h,
                                        }
                                      : s
                                  )
                                )
                              }
                              style={{
                                zIndex: 10,
                                cursor: "move",
                                border: "1px dashed #2196f3",
                              }}
                            >
                              {/* delete btn */}
                              <Box
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSig(sig.id);
                                }}
                                sx={{
                                  position: "absolute",
                                  top: -10,
                                  right: -10,
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  background: "#e53935",
                                  color: "#fff",
                                  fontSize: 14,
                                  lineHeight: "22px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  zIndex: 20,
                                }}
                              >
                                ✕
                              </Box>

                              <img
                                src={`data:image/png;base64,${sig.imgB64}`}
                                alt={sig.name}
                                style={{ width: "100%", height: "100%" }}
                                draggable={false}
                              />
                            </Rnd>
                          );
                        })}
                    </Box>
                  )}
                </Box>
              </Document>
            </Box>
          </Grid>

          {/* controls */}
          <Grid item sm={4} container direction="column" spacing={2}>
            <Grid item>
              {people.map((p) => (
                <Button
                  key={p.name}
                  variant="contained"
                  sx={{ mr: 1, mb: 1 }}
                  onClick={() => addSig(p)}
                >
                  Add {p.name}
                </Button>
              ))}
            </Grid>

            <Grid item>
              <Typography>Page</Typography>
              <Grid container spacing={1} mt={1}>
                {Array.from({ length: numPages }).map((_, i) => (
                  <Grid item key={i}>
                    <Button
                      variant={pageNum === i + 1 ? "outlined" : "contained"}
                      onClick={() => setPageNum(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item>
              <Typography>Zoom (%)</Typography>
              <Slider
                value={zoom}
                min={50}
                max={300}
                valueLabelDisplay="auto"
                onChange={(_, v) => {
                  cookies.set("zoom", v);
                  setZoom(v);
                }}
              />
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                disabled={sigs.length === 0}
                onClick={embedAndDownload}
              >
                Download File
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
